import { AreaId } from 'src/types/area';
import { ProfessionId, ProfessionName } from 'src/types/profession';
import { ProviderDto } from 'src/types/provider';
import { SkillDto } from 'src/types/skill';
import { StreamDto } from 'src/types/stream';

import {
    EdProgramAgeInterval,
    EdProgramFormatFilter,
    EdProgramCoursePurposeFilter,
    EdProgramEducationLevelFilter,
    EdProgramEducationPurposeFilter,
    EdProgramPurpose,
} from 'src/types/EdProgram';
import { EducationProgramAreaDto } from 'src/types/EdProgram/educationProgramAdmin';

export enum EdProgramCategory {
    Courses = 'COURSES',
    Education = 'EDUCATION',
}

interface EdProgramFilterCellStateDto {
    count: number;
    selected: boolean;
    disabled: boolean;
}

export interface EdProgramFilterStateDto {
    state: EdProgramFilterCellStateDto;
}

export interface EdProgramFilterDurationDto {
    id: number;
    durationAlias: string;
}

export enum EdProgramFilterAvailableSortDto {
    Relevant = 'RELEVANT',
    Default = 'DEFAULT',
    Short = 'SHORT',
    Long = 'LONG',
    Priceup = 'PRICE_UP',
    Pricedown = 'PRICE_DOWN',
}

export enum EdProgramPriceInterval {
    FREE = 'FREE',
    CHEAP = 'CHEAP',
}

export interface EdProgramFilterCategoryDto extends EdProgramFilterStateDto {
    category: {
        alias: string;
        name: EdProgramCategory;
    };
}
interface EdProgramFilterPurposeDto extends EdProgramFilterStateDto {
    value: EdProgramCoursePurposeFilter;
    name: string;
}

interface EdProgramFilterStreamDto extends EdProgramFilterStateDto {
    stream: StreamDto;
}

interface EdProgramFilterSkillsDto extends EdProgramFilterStateDto {
    skill: SkillDto;
}

interface EdProgramFilterProvidersDto extends EdProgramFilterStateDto {
    provider: ProviderDto;
}

interface EdProgramFilterDurationsDto extends EdProgramFilterStateDto {
    duration: EdProgramFilterDurationDto;
}

interface EdProgramFilterProfessionsDto extends EdProgramFilterStateDto {
    profession: {
        id: ProfessionId;
        name: ProfessionName;
    };
}

interface EdProgramFilterFormatDto extends EdProgramFilterStateDto {
    value: EdProgramFormatFilter;
    name: string;
}

interface EdProgramFilterAreasDto extends EdProgramFilterStateDto {
    area: EducationProgramAreaDto;
}

interface EdProgramFilterAgeDto extends EdProgramFilterStateDto {
    ageLimit: { name: EdProgramAgeInterval; alias: string };
}

interface EdProgramFilterEducationLevelDto extends EdProgramFilterStateDto {
    value: {
        alias: string;
        name: EdProgramEducationLevelFilter;
    };
}

interface EdProgramFilterPriceIntervalDto extends EdProgramFilterStateDto {
    priceFilterResponse: {
        name: EdProgramPriceInterval;
        alias: string;
    };
}

export interface EdProgramFilterDto {
    filterCategory: EdProgramFilterCategoryDto[];
    filterAge: EdProgramFilterAgeDto[];
    filterPurposes: EdProgramFilterPurposeDto[];
    filterEducationLevel: EdProgramFilterEducationLevelDto[];
    filterFormat: EdProgramFilterFormatDto[];
    filterAreas: EdProgramFilterAreasDto[];
    filterStreams: EdProgramFilterStreamDto[];
    filterSkills: EdProgramFilterSkillsDto[];
    filterProviders: EdProgramFilterProvidersDto[];
    filterDurations: EdProgramFilterDurationsDto[];
    filterProfessions: EdProgramFilterProfessionsDto[];
    availableSortValue: EdProgramFilterAvailableSortDto[];
    filterPriceParameters: EdProgramFilterPriceIntervalDto[];
}

export enum EdProgramFilterItemKey {
    FilterAge = 'filterAge',
    FilterPurposes = 'filterPurposes',
    FilterEducationLevel = 'filterEducationLevel',
    FilterFormat = 'filterFormat',
    FilterAreas = 'filterAreas',
    FilterStreams = 'filterStreams',
    FilterSkills = 'filterSkills',
    FilterProviders = 'filterProviders',
    FilterDurations = 'filterDurations',
    FilterProfessions = 'filterProfessions',
    FilterPriceParameters = 'filterPriceParameters',
}

export type EdProgramFilterId = string;

export interface EdProgramFilterItem extends EdProgramFilterStateDto {
    id: EdProgramFilterId;
    value: string;
    name: string;
    icon?: string;
    type: EdProgramFilterItemKey;
}

export enum EdProgramFilterType {
    Radio = 'radio',
    Checkbox = 'checkbox',
}

export const EdProgramFilterItemKeyTypeMap: Record<EdProgramFilterItemKey, EdProgramFilterType> = {
    [EdProgramFilterItemKey.FilterPurposes]: EdProgramFilterType.Radio,
    [EdProgramFilterItemKey.FilterEducationLevel]: EdProgramFilterType.Radio,
    [EdProgramFilterItemKey.FilterAge]: EdProgramFilterType.Radio,
    [EdProgramFilterItemKey.FilterFormat]: EdProgramFilterType.Radio,
    [EdProgramFilterItemKey.FilterAreas]: EdProgramFilterType.Checkbox,
    [EdProgramFilterItemKey.FilterStreams]: EdProgramFilterType.Checkbox,
    [EdProgramFilterItemKey.FilterSkills]: EdProgramFilterType.Checkbox,
    [EdProgramFilterItemKey.FilterProviders]: EdProgramFilterType.Checkbox,
    [EdProgramFilterItemKey.FilterDurations]: EdProgramFilterType.Checkbox,
    [EdProgramFilterItemKey.FilterProfessions]: EdProgramFilterType.Checkbox,
    [EdProgramFilterItemKey.FilterPriceParameters]: EdProgramFilterType.Checkbox,
} as const;

export const EdProgramFilterItemKeyHasSearch: Partial<Record<EdProgramFilterItemKey, boolean>> = {
    [EdProgramFilterItemKey.FilterProfessions]: true,
    [EdProgramFilterItemKey.FilterSkills]: true,
    [EdProgramFilterItemKey.FilterAreas]: true,
    [EdProgramFilterItemKey.FilterProviders]: true,
};

export const EdProgramFilterItemKeyCheckboxToggleMobile: Partial<Record<EdProgramFilterItemKey, boolean>> = {
    [EdProgramFilterItemKey.FilterPriceParameters]: true,
};

export type EdProgramFilter = Partial<Record<EdProgramFilterItemKey, EdProgramFilterItem[]>>;

export type EdProgramFilterSearch = string;

export interface EdProgramFilterQuery<
    TypePurpose extends string = `${EdProgramCoursePurposeFilter}` | `${EdProgramEducationPurposeFilter}`,
> {
    category?: EdProgramCategory;
    educationLevel?: EdProgramEducationLevelFilter;
    priceParameters?: EdProgramPriceInterval[];
    purpose?: TypePurpose;
    text?: EdProgramFilterSearch;
    ageLimit?: EdProgramAgeInterval;
    format?: EdProgramFormatFilter;
    areaIds?: AreaId[];
    professionIds?: string[];
    streamIds?: string[];
    skillIds?: string[];
    providerIds?: string[];
    duration?: string[];
    sort?: EdProgramFilterAvailableSortDto;
}

export type EdProgramFilterQueryForListShard = EdProgramFilterQuery & {
    category: EdProgramCategory;
    purpose: EdProgramPurpose;
    page: number;
};

export type EdProgramFilterQueryForFiltersShard = EdProgramFilterQuery;
